import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import useGet from 'hooks/useGet';
import useCreate from 'hooks/useCreate';
import useUpdate from 'hooks/useUpdate';
import useDelete from 'hooks/useDelete';
import API_BASE_URL from 'config/api';
import { useAuth } from 'pages/AuthProvider';

const normalizeArray = (data, keys = []) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data[key])) return data[key];
  }
  return [];
};

const getCategoryName = (category, categoriesMap) => {
  if (!category) return '-';
  if (typeof category === 'string') return categoriesMap.get(category) || category;
  if (typeof category === 'object') {
    return category.name || category.categoryName || category.title || category._id || '-';
  }
  return '-';
};

const createEmptyCondition = () => ({
  operator: 'lte',
  value: ''
});

const OPERATOR_OPTIONS = [
  { value: 'eq', label: 'Equals' },
  { value: 'lte', label: 'Less than or equal to' },
  { value: 'gte', label: 'Greater than or equal to' },
  { value: 'lt', label: 'Less than' },
  { value: 'gt', label: 'Greater than' },
  { value: 'in', label: 'In list (comma separated)' }
];

const TAG_CATEGORY_REGEX = /^(gifts|collection|ready[\s-]*to[\s-]*ship)$/i;

const resolveImageUrl = (item) => {
  const raw =
    item?.image?.url ||
    item?.image?.path ||
    item?.imageUrl ||
    item?.thumbnail ||
    item?.photo ||
    item?.image;
  if (!raw || typeof raw !== 'string') return '';
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  const normalized = raw.replace(/^\/+/, '');
  if (normalized.startsWith('uploads/')) return `${API_BASE_URL}/${normalized}`;
  if (raw.startsWith('/')) return `${API_BASE_URL}${raw}`;
  return `${API_BASE_URL}/uploads/${normalized}`;
};

const resolveBannerDesktopUrl = (item) => {
  const raw =
    item?.bannerImageDesktop?.url ||
    item?.bannerImageDesktop?.path ||
    item?.bannerImageDesktopUrl ||
    item?.bannerImageDesktop;
  if (!raw || typeof raw !== 'string') return '';
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  const normalized = raw.replace(/^\/+/, '');
  if (normalized.startsWith('uploads/')) return `${API_BASE_URL}/${normalized}`;
  if (raw.startsWith('/')) return `${API_BASE_URL}${raw}`;
  return `${API_BASE_URL}/uploads/${normalized}`;
};

const resolveBannerMobileUrl = (item) => {
  const raw =
    item?.bannerImageMobile?.url ||
    item?.bannerImageMobile?.path ||
    item?.bannerImageMobileUrl ||
    item?.bannerImageMobile;
  if (!raw || typeof raw !== 'string') return '';
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  const normalized = raw.replace(/^\/+/, '');
  if (normalized.startsWith('uploads/')) return `${API_BASE_URL}/${normalized}`;
  if (raw.startsWith('/')) return `${API_BASE_URL}${raw}`;
  return `${API_BASE_URL}/uploads/${normalized}`;
};

const parseConditionValue = (operator, value) => {
  if (operator === 'in') {
    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (['lte', 'gte', 'lt', 'gt'].includes(operator)) {
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
  }

  return value;
};

export default function Tags() {
  const { canAccess } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingTagId, setEditingTagId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [bannerDesktopFile, setBannerDesktopFile] = useState(null);
  const [bannerDesktopPreview, setBannerDesktopPreview] = useState('');
  const [bannerMobileFile, setBannerMobileFile] = useState(null);
  const [bannerMobilePreview, setBannerMobilePreview] = useState('');
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    type: 'manual',
    currency: 'INR',
    isActive: true,
    conditions: [createEmptyCondition()]
  });

  const {
    data: tagsData,
    loading: tagsLoading,
    error: tagsError
  } = useGet('/api/tag');
  const {
    data: categoriesData,
    loading: categoriesLoading
  } = useGet('/api/categories');
  const { create } = useCreate('/api/tag');
  const { update } = useUpdate('/api/tag');
  const { remove } = useDelete('/api/tag');

  const tags = useMemo(
    () => normalizeArray(tagsData, ['tags', 'data', 'items']),
    [tagsData]
  );
  const categories = useMemo(
    () => normalizeArray(categoriesData, ['categories', 'data', 'items']),
    [categoriesData]
  );
  const tagCategories = useMemo(
    () =>
      categories.filter((cat) => {
        const name = cat?.name || cat?.categoryName || cat?.title || '';
        return TAG_CATEGORY_REGEX.test(String(name).trim());
      }),
    [categories]
  );
  const categoriesMap = useMemo(() => {
    const map = new Map();
    categories.forEach((cat) => {
      const id = cat?._id || cat?.id;
      if (id) map.set(id, cat.name || cat.categoryName || cat.title || id);
    });
    return map;
  }, [categories]);

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      if (bannerDesktopPreview && bannerDesktopPreview.startsWith('blob:')) {
        URL.revokeObjectURL(bannerDesktopPreview);
      }
      if (bannerMobilePreview && bannerMobilePreview.startsWith('blob:')) {
        URL.revokeObjectURL(bannerMobilePreview);
      }
    };
  }, [imagePreview, bannerDesktopPreview, bannerMobilePreview]);

  const resetForm = () => {
    setFormData({
      category: '',
      name: '',
      type: 'manual',
      currency: 'INR',
      isActive: true,
      conditions: [createEmptyCondition()]
    });
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    if (bannerDesktopPreview && bannerDesktopPreview.startsWith('blob:')) {
      URL.revokeObjectURL(bannerDesktopPreview);
    }
    if (bannerMobilePreview && bannerMobilePreview.startsWith('blob:')) {
      URL.revokeObjectURL(bannerMobilePreview);
    }
    setImagePreview('');
    setImageFile(null);
    setBannerDesktopPreview('');
    setBannerDesktopFile(null);
    setBannerMobilePreview('');
    setBannerMobileFile(null);
    setEditingTagId(null);
    setErrorMessage('');
  };

  const handleOpen = () => {
    resetForm();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleEdit = (tag) => {
    const categoryId =
      typeof tag.category === 'object'
        ? tag.category?._id || tag.category?.id || ''
        : tag.category || '';
    const tagConditions = Array.isArray(tag.conditions) && tag.conditions.length > 0
      ? tag.conditions.map((condition) => ({
          operator: condition.operator || 'lte',
          value: Array.isArray(condition.value) ? condition.value.join(', ') : String(condition.value ?? '')
        }))
      : [createEmptyCondition()];

    setEditingTagId(tag._id || tag.id || null);
    setFormData({
      category: categoryId,
      name: tag.name || '',
      type: tag.type || 'manual',
      currency: tag.currency || 'INR',
      isActive: tag.isActive !== false,
      conditions: tagConditions
    });
    setImageFile(null);
    setImagePreview(resolveImageUrl(tag));
    setBannerDesktopFile(null);
    setBannerDesktopPreview(resolveBannerDesktopUrl(tag));
    setBannerMobileFile(null);
    setBannerMobilePreview(resolveBannerMobileUrl(tag));
    setErrorMessage('');
    setOpen(true);
  };

  const handleDelete = async (tag) => {
    const id = tag._id || tag.id;
    if (!id) return;
    const confirmed = window.confirm(`Delete tag "${tag.name || 'this tag'}"?`);
    if (!confirmed) return;

    try {
      await remove(id);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Failed to delete tag.');
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleBannerDesktopChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (bannerDesktopPreview && bannerDesktopPreview.startsWith('blob:')) {
      URL.revokeObjectURL(bannerDesktopPreview);
    }
    setBannerDesktopFile(file);
    setBannerDesktopPreview(URL.createObjectURL(file));
  };

  const handleBannerMobileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (bannerMobilePreview && bannerMobilePreview.startsWith('blob:')) {
      URL.revokeObjectURL(bannerMobilePreview);
    }
    setBannerMobileFile(file);
    setBannerMobilePreview(URL.createObjectURL(file));
  };

  const updateCondition = (index, key, value) => {
    setFormData((prev) => ({
      ...prev,
      conditions: prev.conditions.map((condition, conditionIndex) =>
        conditionIndex === index ? { ...condition, [key]: value } : condition
      )
    }));
  };

  const addCondition = () => {
    setFormData((prev) => ({
      ...prev,
      conditions: [...prev.conditions, createEmptyCondition()]
    }));
  };

  const removeCondition = (index) => {
    setFormData((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, conditionIndex) => conditionIndex !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.category || !formData.name.trim()) {
      setErrorMessage('Category and tag name are required.');
      return;
    }

    const cleanedConditions = formData.conditions
      .filter((condition) => condition.operator)
      .map((condition) => ({
        field: 'price',
        operator: condition.operator,
        value: parseConditionValue(condition.operator, condition.value)
      }));

    if (formData.type === 'dynamic' && cleanedConditions.length === 0) {
      setErrorMessage('At least one condition is required for dynamic tags.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    try {
      const payload = {
        name: formData.name.trim(),
        category: formData.category,
        type: formData.type,
        currency: formData.currency,
        isActive: Boolean(formData.isActive),
        conditions: formData.type === 'dynamic' ? cleanedConditions : []
      };
      const hasFile = Boolean(imageFile || bannerDesktopFile || bannerMobileFile);
      const finalPayload = hasFile
        ? (() => {
            const data = new FormData();
            data.append('name', payload.name);
            data.append('category', payload.category);
            data.append('type', payload.type);
            data.append('currency', payload.currency);
            data.append('isActive', String(payload.isActive));
            data.append('conditions', JSON.stringify(payload.conditions || []));
            if (imageFile) data.append('image', imageFile);
            if (bannerDesktopFile) data.append('bannerImageDesktop', bannerDesktopFile);
            if (bannerMobileFile) data.append('bannerImageMobile', bannerMobileFile);
            return data;
          })()
        : payload;

      if (editingTagId) {
        await update(editingTagId, finalPayload);
      } else {
        await create(finalPayload);
      }
      handleClose();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Failed to save tag.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="h5">Tags</Typography>
        {/* <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
          Add Tag
        </Button> */}
      </Box>

      {tagsError && (
        <Typography color="error" sx={{ mb: 2 }}>
          Failed to load tags.
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tagsLoading ? (
              <TableRow>
                <TableCell colSpan={5}>Loading tags...</TableCell>
              </TableRow>
            ) : tags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>No tags found.</TableCell>
              </TableRow>
            ) : (
              tags.map((tag) => (
                <TableRow key={tag._id || tag.id || `${tag.name}-${tag.category}`}>
                  <TableCell>{tag.name || '-'}</TableCell>
                  <TableCell>{tag.type || '-'}</TableCell>
                  <TableCell>{getCategoryName(tag.category, categoriesMap)}</TableCell>
                  <TableCell>{tag.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    {canAccess('tags', 'edit') && (
                      <IconButton color="primary" size="small" onClick={() => handleEdit(tag)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canAccess('tags', 'delete') && (
                      <IconButton color="error" size="small" onClick={() => handleDelete(tag)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingTagId ? 'Edit Tag' : 'Add Tag'}</DialogTitle>
        <DialogContent sx={{ pt: 1, display: 'grid', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="tag-category-label">Category</InputLabel>
            <Select
              labelId="tag-category-label"
              label="Category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              disabled={categoriesLoading}
            >
              {categoriesLoading ? (
                <MenuItem disabled>Loading categories...</MenuItem>
              ) : tagCategories.length === 0 ? (
                <MenuItem disabled>No categories found</MenuItem>
              ) : (
                tagCategories.map((cat) => (
                  <MenuItem key={cat._id || cat.id} value={cat._id || cat.id}>
                    {cat.name || cat.categoryName || cat.title || 'Unnamed'}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            label="Tag Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
          />

          <Box sx={{ display: 'grid', gap: 1 }}>
            <Button variant="outlined" component="label">
              Upload Image
              <input type="file" hidden accept="image/*" onChange={handleImageChange} />
            </Button>
            {imagePreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img
                  src={imagePreview}
                  alt="tag preview"
                  style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {imageFile ? imageFile.name : 'Current image'}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Box sx={{ display: 'grid', gap: 1 }}>
            <Button variant="outlined" component="label">
              Upload Banner (Desktop)
              <input type="file" hidden accept="image/*" onChange={handleBannerDesktopChange} />
            </Button>
            {bannerDesktopPreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img
                  src={bannerDesktopPreview}
                  alt="tag banner desktop preview"
                  style={{ width: 120, height: 56, borderRadius: 8, objectFit: 'cover' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {bannerDesktopFile ? bannerDesktopFile.name : 'Current desktop banner'}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Box sx={{ display: 'grid', gap: 1 }}>
            <Button variant="outlined" component="label">
              Upload Banner (Mobile)
              <input type="file" hidden accept="image/*" onChange={handleBannerMobileChange} />
            </Button>
            {bannerMobilePreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img
                  src={bannerMobilePreview}
                  alt="tag banner mobile preview"
                  style={{ width: 96, height: 56, borderRadius: 8, objectFit: 'cover' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {bannerMobileFile ? bannerMobileFile.name : 'Current mobile banner'}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <FormControl fullWidth>
            <InputLabel id="tag-type-label">Type</InputLabel>
            <Select
              labelId="tag-type-label"
              label="Type"
              name="type"
              value={formData.type}
              onChange={handleChange}
            >
              <MenuItem value="manual">manual</MenuItem>
              <MenuItem value="dynamic">dynamic</MenuItem>
              <MenuItem value="system">system</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="tag-currency-label">Currency</InputLabel>
            <Select
              labelId="tag-currency-label"
              label="Currency"
              name="currency"
              value={formData.currency}
              onChange={handleChange}
            >
              <MenuItem value="USD">US (USD)</MenuItem>
              <MenuItem value="INR">IN (INR)</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="tag-active-label">Status</InputLabel>
            <Select
              labelId="tag-active-label"
              label="Status"
              name="isActive"
              value={formData.isActive ? 'active' : 'inactive'}
              onChange={(event) => {
                setFormData((prev) => ({
                  ...prev,
                  isActive: event.target.value === 'active'
                }));
              }}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          {formData.type === 'dynamic' && (
            <>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2">Conditions</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>
                  Add Condition
                </Button>
              </Box>

              {formData.conditions.map((condition, index) => (
                <Box key={`condition-${index}`} sx={{ display: 'grid', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Field: Price
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel id={`tag-condition-operator-${index}`}>Operator</InputLabel>
                    <Select
                      labelId={`tag-condition-operator-${index}`}
                      label="Operator"
                      value={condition.operator}
                      onChange={(event) => updateCondition(index, 'operator', event.target.value)}
                    >
                      {OPERATOR_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      label={condition.operator === 'in' ? 'Value (comma separated)' : 'Value'}
                      value={condition.value}
                      onChange={(event) => updateCondition(index, 'value', event.target.value)}
                      fullWidth
                    />
                    <IconButton
                      onClick={() => removeCondition(index)}
                      disabled={formData.conditions.length === 1}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </>
          )}

          {errorMessage && <Typography color="error">{errorMessage}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : editingTagId ? 'Update Tag' : 'Save Tag'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
